import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/Button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Progress } from '../ui/progress';
import { 
  Calendar,
  Clock,
  Users,
  FileText,
  Phone,
  Video,
  MapPin,
  CheckCircle,
  AlertCircle,
  XCircle,
  Star,
  TrendingUp,
  Settings,
  Bell,
  Search,
  Filter,
  Download,
  Upload,
  Edit,
  Eye,
  MessageSquare,
  Building2,
  Stethoscope,
  Brain,
  Heart,
  Activity,
  UserCheck,
  CalendarDays,
  ClipboardList,
  DollarSign,
  BarChart3,
  PieChart,
  Target,
  Award,
  Zap,
  Mail,
  Plus
} from 'lucide-react';

// Mock data for demonstration
const mockReferrals = [
  {
    id: 'REF-001',
    patientName: 'Margaret Thompson',
    age: 78,
    facility: 'Sunny Meadows Assisted Living',
    contactPerson: 'Sarah Johnson, RN',
    phone: '(555) 234-5678',
    referralType: 'Psychiatric Evaluation',
    urgency: 'urgent',
    dateSubmitted: '2024-01-20',
    status: 'pending',
    clinicalNotes: 'Patient showing increased agitation and sleep disturbances over past 2 weeks. Previous psychiatric history includes mild depression. Current medications include Sertraline 50mg daily.',
    currentMedications: 'Sertraline 50mg daily, Lisinopril 10mg daily, Metformin 500mg BID',
    allergies: 'NKDA',
    insurance: 'Medicare',
    scheduledDate: null
  },
  {
    id: 'REF-002',
    patientName: 'Robert Chen',
    age: 65,
    facility: 'Oakwood Rehabilitation Center',
    contactPerson: 'Michael Rodriguez, LCSW',
    phone: '(555) 345-6789',
    referralType: 'Medication Management',
    urgency: 'routine',
    dateSubmitted: '2024-01-18',
    status: 'accepted',
    clinicalNotes: 'Follow-up for medication adjustment. Patient reports improved mood but experiencing some side effects.',
    currentMedications: 'Escitalopram 20mg daily, Trazodone 50mg HS',
    allergies: 'Penicillin',
    insurance: 'Medicaid',
    scheduledDate: '2024-01-25'
  },
  {
    id: 'REF-003',
    patientName: 'Eleanor Williams',
    age: 82,
    facility: 'Heritage Manor Nursing Home',
    contactPerson: 'Dr. Patricia Lee',
    phone: '(555) 456-7890',
    referralType: 'Crisis Intervention',
    urgency: 'emergent',
    dateSubmitted: '2024-01-22',
    status: 'completed',
    clinicalNotes: 'Emergency consultation for severe behavioral disturbance. Patient attempted to leave facility multiple times.',
    currentMedications: 'Donepezil 10mg daily, Memantine 10mg BID',
    allergies: 'Sulfa drugs',
    insurance: 'Medicare + Supplemental',
    scheduledDate: '2024-01-22'
  }
];

const mockAppointments = [
  {
    id: 'APT-001',
    patientName: 'Robert Chen',
    facility: 'Oakwood Rehabilitation Center',
    type: 'Medication Management',
    date: '2024-01-25',
    time: '10:00 AM',
    duration: 45,
    status: 'scheduled',
    mode: 'onsite'
  },
  {
    id: 'APT-002',
    patientName: 'Dorothy Martinez',
    facility: 'Sunset Gardens',
    type: 'Follow-up Evaluation',
    date: '2024-01-25',
    time: '2:00 PM',
    duration: 30,
    status: 'scheduled',
    mode: 'telehealth'
  },
  {
    id: 'APT-003',
    patientName: 'James Wilson',
    facility: 'Mountain View Care',
    type: 'Initial Consultation',
    date: '2024-01-26',
    time: '9:00 AM',
    duration: 60,
    status: 'scheduled',
    mode: 'onsite'
  }
];

const mockMessages = [
  {
    id: 'MSG-001',
    from: 'Sarah Johnson, RN',
    facility: 'Sunny Meadows Assisted Living',
    subject: 'Follow-up on Margaret Thompson',
    message: 'Patient is showing improvement since medication adjustment. Sleep patterns have normalized.',
    timestamp: '2024-01-23 2:30 PM',
    unread: true
  },
  {
    id: 'MSG-002',
    from: 'Dr. Patricia Lee',
    facility: 'Heritage Manor Nursing Home',
    subject: 'Thank you for emergency consultation',
    message: 'Your prompt response and intervention helped stabilize the situation. Patient is much calmer now.',
    timestamp: '2024-01-22 6:45 PM',
    unread: false
  }
];

export const ProviderDashboard = () => {
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'emergent': return 'bg-red-100 text-red-800';
      case 'urgent': return 'bg-amber-100 text-amber-800';
      case 'routine': return 'bg-green-100 text-green-800';
      default: return 'bg-clinic-gray-100 text-clinic-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800';
      case 'accepted': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'declined': return 'bg-red-100 text-red-800';
      default: return 'bg-clinic-gray-100 text-clinic-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <AlertCircle className="w-4 h-4" />;
      case 'accepted': return <CheckCircle className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'declined': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const handleReferralAction = (referralId, action: 'accept' | 'decline') => {
    console.log(`${action} referral:`, referralId);
    // Handle referral action
  };

  const filteredReferrals = mockReferrals.filter(referral => {
    const matchesStatus = filterStatus === 'all' || referral.status === filterStatus;
    const matchesSearch = referral.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         referral.facility.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-clinic-blue-50 to-clinic-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-clinic-gray-900 mb-2">
                Provider Dashboard
              </h1>
              <p className="text-lg text-clinic-gray-600">
                Welcome back, Dr. Sarah Smith
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="outline" className="btn-md">
                <Bell className="w-4 h-4 mr-2" />
                Notifications (3)
              </Button>
              <Button variant="outline" className="btn-md">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-clinic-gray-600 mb-1">Pending Referrals</p>
                    <p className="text-3xl font-bold text-clinic-gray-900">5</p>
                    <p className="text-xs text-amber-600 flex items-center mt-1">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      2 urgent
                    </p>
                  </div>
                  <div className="icon-container icon-container-lg icon-warning">
                    <ClipboardList className="icon-xl" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-clinic-gray-600 mb-1">Today's Appointments</p>
                    <p className="text-3xl font-bold text-clinic-gray-900">3</p>
                    <p className="text-xs text-green-600 flex items-center mt-1">
                      <Calendar className="w-3 h-3 mr-1" />
                      2 onsite, 1 telehealth
                    </p>
                  </div>
                  <div className="icon-container icon-container-lg icon-medical">
                    <CalendarDays className="icon-xl" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-clinic-gray-600 mb-1">This Month</p>
                    <p className="text-3xl font-bold text-clinic-gray-900">47</p>
                    <p className="text-xs text-green-600 flex items-center mt-1">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      +12% vs last month
                    </p>
                  </div>
                  <div className="icon-container icon-container-lg icon-success">
                    <BarChart3 className="icon-xl" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-clinic-gray-600 mb-1">Patient Satisfaction</p>
                    <p className="text-3xl font-bold text-clinic-gray-900">4.9</p>
                    <p className="text-xs text-green-600 flex items-center mt-1">
                      <Star className="w-3 h-3 mr-1" />
                      98% positive reviews
                    </p>
                  </div>
                  <div className="icon-container icon-container-lg icon-success">
                    <Award className="icon-xl" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="referrals" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="referrals">Referrals</TabsTrigger>
            <TabsTrigger value="appointments">Schedule</TabsTrigger>
            <TabsTrigger value="patients">Patients</TabsTrigger>
            <TabsTrigger value="messages">Messages</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

          {/* Referrals Tab */}
          <TabsContent value="referrals" className="space-y-6">
            <Card className="card-elevated">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-3">
                      <ClipboardList className="icon-lg text-clinic-blue-600" />
                      Facility Referrals
                    </CardTitle>
                    <CardDescription>
                      Review and manage incoming referrals from healthcare facilities
                    </CardDescription>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-clinic-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search patients or facilities..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-64"
                      />
                    </div>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-40">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="accepted">Accepted</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="declined">Declined</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredReferrals.map((referral) => (
                    <Card key={referral.id} className="border-l-4 border-l-clinic-blue-500 hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-clinic-gray-900">
                                {referral.patientName}
                              </h3>
                              <Badge className={getUrgencyColor(referral.urgency)}>
                                {referral.urgency}
                              </Badge>
                              <Badge className={getStatusColor(referral.status)}>
                                <div className="flex items-center gap-1">
                                  {getStatusIcon(referral.status)}
                                  {referral.status}
                                </div>
                              </Badge>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4 text-sm text-clinic-gray-600">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4" />
                                {referral.facility}
                              </div>
                              <div className="flex items-center gap-2">
                                <UserCheck className="w-4 h-4" />
                                {referral.contactPerson}
                              </div>
                              <div className="flex items-center gap-2">
                                <Stethoscope className="w-4 h-4" />
                                {referral.referralType}
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Submitted: {referral.dateSubmitted}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {referral.status === 'pending' && (
                              <>
                                <Button
                                  onClick={() => handleReferralAction(referral.id, 'accept')}
                                  className="btn-sm bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Accept
                                </Button>
                                <Button
                                  onClick={() => handleReferralAction(referral.id, 'decline')}
                                  variant="outline"
                                  className="btn-sm border-red-300 text-red-600 hover:bg-red-50"
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Decline
                                </Button>
                              </>
                            )}
                            <Button
                              variant="outline"
                              className="btn-sm"
                              onClick={() => setSelectedReferral(selectedReferral === referral.id ? null : referral.id)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Details
                            </Button>
                          </div>
                        </div>

                        {selectedReferral === referral.id && (
                          <div className="mt-4 pt-4 border-t border-clinic-gray-200 space-y-4">
                            <div>
                              <h4 className="font-medium text-clinic-gray-800 mb-2">Clinical Notes:</h4>
                              <p className="text-clinic-gray-700 bg-clinic-gray-50 p-3 rounded-lg">
                                {referral.clinicalNotes}
                              </p>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <h4 className="font-medium text-clinic-gray-800 mb-2">Current Medications:</h4>
                                <p className="text-clinic-gray-700 bg-clinic-blue-50 p-3 rounded-lg">
                                  {referral.currentMedications}
                                </p>
                              </div>
                              <div>
                                <h4 className="font-medium text-clinic-gray-800 mb-2">Allergies:</h4>
                                <p className="text-clinic-gray-700 bg-clinic-gold-50 p-3 rounded-lg">
                                  {referral.allergies}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between pt-3">
                              <div className="text-sm text-clinic-gray-600">
                                <span className="font-medium">Insurance:</span> {referral.insurance}
                              </div>
                              <div className="flex items-center space-x-2">
                                <Button variant="outline" className="btn-sm">
                                  <Phone className="w-4 h-4 mr-1" />
                                  Call Facility
                                </Button>
                                <Button variant="outline" className="btn-sm">
                                  <MessageSquare className="w-4 h-4 mr-1" />
                                  Send Message
                                </Button>
                                {referral.status === 'accepted' && (
                                  <Button className="btn-sm bg-clinic-blue-600 hover:bg-clinic-blue-700">
                                    <Calendar className="w-4 h-4 mr-1" />
                                    Schedule
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <CalendarDays className="icon-lg text-clinic-blue-600" />
                      Today's Schedule
                    </CardTitle>
                    <CardDescription>Thursday, January 25, 2024</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {mockAppointments.map((appointment) => (
                      <Card key={appointment.id} className="border-l-4 border-l-clinic-blue-500">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-semibold text-clinic-gray-900">
                                  {appointment.patientName}
                                </h3>
                                <Badge className={appointment.mode === 'telehealth' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                                  {appointment.mode === 'telehealth' ? (
                                    <Video className="w-3 h-3 mr-1" />
                                  ) : (
                                    <MapPin className="w-3 h-3 mr-1" />
                                  )}
                                  {appointment.mode}
                                </Badge>
                              </div>
                              <div className="grid md:grid-cols-2 gap-2 text-sm text-clinic-gray-600">
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4" />
                                  {appointment.facility}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Stethoscope className="w-4 h-4" />
                                  {appointment.type}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4" />
                                  {appointment.time} ({appointment.duration} min)
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {appointment.mode === 'telehealth' ? (
                                <Button className="btn-sm bg-purple-600 hover:bg-purple-700">
                                  <Video className="w-4 h-4 mr-1" />
                                  Join Call
                                </Button>
                              ) : (
                                <Button className="btn-sm bg-clinic-blue-600 hover:bg-clinic-blue-700">
                                  <MapPin className="w-4 h-4 mr-1" />
                                  Get Directions
                                </Button>
                              )}
                              <Button variant="outline" className="btn-sm">
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Clock className="icon-lg text-clinic-gold-600" />
                      Quick Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button className="w-full btn-md bg-clinic-blue-600 hover:bg-clinic-blue-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Block Time Off
                    </Button>
                    <Button variant="outline" className="w-full btn-md">
                      <Calendar className="w-4 h-4 mr-2" />
                      View Full Calendar
                    </Button>
                    <Button variant="outline" className="w-full btn-md">
                      <Settings className="w-4 h-4 mr-2" />
                      Availability Settings
                    </Button>
                  </CardContent>
                </Card>

                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Target className="icon-lg text-clinic-gold-600" />
                      This Week
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Appointments</span>
                        <span>15/20</span>
                      </div>
                      <Progress value={75} className="h-2" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Availability</span>
                        <span>32 hours</span>
                      </div>
                      <Progress value={80} className="h-2" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Patients Tab */}
          <TabsContent value="patients" className="space-y-6">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <Users className="icon-lg text-clinic-blue-600" />
                  Active Patients
                </CardTitle>
                <CardDescription>
                  Patients currently under your care
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <div className="icon-container icon-container-2xl icon-medical mx-auto mb-4">
                    <Users className="icon-3xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-clinic-gray-900 mb-2">
                    Patient Management Coming Soon
                  </h3>
                  <p className="text-clinic-gray-600 max-w-md mx-auto">
                    Comprehensive patient records, treatment plans, and progress tracking will be available here.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-6">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <MessageSquare className="icon-lg text-clinic-blue-600" />
                  Facility Communications
                </CardTitle>
                <CardDescription>
                  Messages from healthcare facilities and staff
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {mockMessages.map((message) => (
                  <Card key={message.id} className={`border-l-4 ${message.unread ? 'border-l-clinic-blue-500 bg-clinic-blue-50' : 'border-l-clinic-gray-300'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-clinic-gray-900">
                            {message.from}
                          </h3>
                          {message.unread && (
                            <Badge className="bg-clinic-blue-100 text-clinic-blue-800">
                              New
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-clinic-gray-500">{message.timestamp}</span>
                      </div>
                      <p className="text-sm text-clinic-gray-600 mb-2">{message.facility}</p>
                      <h4 className="font-medium text-clinic-gray-800 mb-2">{message.subject}</h4>
                      <p className="text-clinic-gray-700">{message.message}</p>
                      <div className="flex items-center space-x-2 mt-3">
                        <Button variant="outline" className="btn-sm">
                          <Mail className="w-4 h-4 mr-1" />
                          Reply
                        </Button>
                        <Button variant="outline" className="btn-sm">
                          <Phone className="w-4 h-4 mr-1" />
                          Call
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <BarChart3 className="icon-lg text-clinic-blue-600" />
                    Monthly Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-clinic-gray-600">Total Appointments</span>
                      <span className="font-semibold">47</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-clinic-gray-600">Referrals Processed</span>
                      <span className="font-semibold">23</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-clinic-gray-600">Telehealth Sessions</span>
                      <span className="font-semibold">18</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-clinic-gray-600">On-site Visits</span>
                      <span className="font-semibold">29</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <PieChart className="icon-lg text-clinic-gold-600" />
                    Referral Sources
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Nursing Homes</span>
                      <span className="text-sm font-medium">45%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Assisted Living</span>
                      <span className="text-sm font-medium">30%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Rehabilitation Centers</span>
                      <span className="text-sm font-medium">25%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Star className="icon-lg text-clinic-success-600" />
                    Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-clinic-gray-900">4.9</div>
                      <div className="text-sm text-clinic-gray-600">Average Rating</div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Response Time</span>
                        <span className="text-green-600">Excellent</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Completion Rate</span>
                        <span className="text-green-600">98%</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};